import { motion } from "framer-motion";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import {
  BsFillCheckCircleFill,
  BsFillExclamationDiamondFill,
  BsFillExclamationTriangleFill,
} from "react-icons/bs";
import { MdClose } from "react-icons/md";

import { Spinner } from "../atoms/Spinner";

export type StatusTypes = "loading" | "warning" | "error" | "success";

export type ToastInfos = {
  status: StatusTypes;
  title: string;
  description?: string;
};

type ToastProps = {
  toast: ToastInfos;
  closeToast: () => void;
};

const Toast: FC<ToastProps> = ({ toast, closeToast }) => {
  const [isVisible, setIsVisible] = useState(true);

  const triggerClose = useCallback(() => {
    setIsVisible(false);
    const removeTimeout = setTimeout(closeToast, 500);
    return () => clearTimeout(removeTimeout);
  }, [closeToast]);

  useEffect(() => {
    if (toast.status !== "loading") {
      const timeout = setTimeout(() => {
        triggerClose();
      }, 5000);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [toast.status, triggerClose]);

  return (
    <div
      className={`fixed z-[99999] top-0 left-0 right-0 m-2 md:top-[72px] md:right-4 md:left-auto md:max-w-xs bg-white shadow-lg rounded-xl p-4 flex items-start ${
        isVisible ? "animate-fade" : "animate-fade-out"
      }`}
    >
      <div className="pr-4">
        {toast.status === "loading" && <Spinner />}
        {toast.status === "warning" && (
          <BsFillExclamationTriangleFill className="text-warning-text h-5 w-5" />
        )}
        {toast.status === "error" && (
          <BsFillExclamationDiamondFill className="text-alert-text h-5 w-5" />
        )}
        {toast.status === "success" && (
          <BsFillCheckCircleFill className="text-success-text h-5 w-5" />
        )}
      </div>
      <div className="flex-1">
        <p className="font-bold">{toast.title}</p>
        {toast.description ? <p>{toast.description}</p> : undefined}
      </div>
      {toast.status !== "loading" && (
        <div className="pl-4">
          <motion.button
            whileTap={{ scale: 0.95, animationDuration: "0.2s" }}
            onClick={triggerClose}
          >
            <MdClose className="text-tertiary-text h-5 w-5" />
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default Toast;
